import { useTranslation } from "react-i18next";
import { CreditCard, ShieldCheck, Sparkles } from "lucide-react";
import {
  contentFor,
  viewerPackagePriceCents,
  formatPrice,
  type Currency,
  type Package,
} from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { PackageArtwork } from "@/features/packages/components/PackageArtwork";
import type { localeOf } from "@/features/shop/shop.catalog";
import { usePriceViewer } from "@/features/shop/usePriceViewer";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Las DOS salidas del paquete que se eligió en `/paquetes`.
 *
 * Anahí (29-ago): "de lado izquierdo está la opción de la franquicia con
 * el precio de descuento de ese paquete y en cuánto te saldría si
 * tuvieras la franquicia, y de lado derecho la info de ese paquete pero
 * con el link de stripe para ya comprar ese paquete en dado caso que no
 * quiera la franquicia."
 *
 * O sea: la pantalla deja de ser un embudo de una sola salida. A la
 * izquierda se argumenta la franquicia CON NÚMEROS —cuánto cuesta hoy
 * este mismo paquete siendo franquiciatario—, y a la derecha se deja
 * abierta la puerta de comprarlo y ya. Esconder la segunda no vende más
 * franquicias: solo hace que quien no la quiere se vaya sin comprar.
 * ═══════════════════════════════════════════════════════════════════
 */

/**
 * Cuánto costaría ESE paquete siendo franquiciatario.
 *
 * ── Contra qué se compara (Anahí, 29-ago) ────────────────────────────
 * "Aquí debe mostrar un monto de descuento porque ahora aparece el mismo
 * precio del paquete." Tenía razón, y la causa estaba en los datos: solo
 * 1 de los 14 paquetes tiene `franchiseeDiscountPercent`, así que para
 * los otros 13 el "precio de franquiciatario" salía idéntico al de la
 * tienda y la card no decía nada.
 *
 * El descuento SÍ existe, pero está en otro campo: un paquete mayorista
 * ya nace rebajado sobre su `listPrices` —HOLOS vale 12,960 de lista y
 * se vende en 9,072, un 30%—. Ese es el ahorro real y es el que se
 * enseña. Comparar contra `prices` era comparar el precio consigo mismo.
 *
 * Y encima de ese precio va el descuento de franquiciatario (Anahí,
 * 14-sep-2026): el 10 % general, o el propio del paquete.
 */
export function precioConFranquicia(pkg: Package, currency: Currency): number | undefined {
  // Desde el 14-sep-2026 el descuento de franquiciatario es de TODOS los
  // paquetes (10 %, o el propio del paquete): la misma función que cobra
  // el servidor cuando el pedido lleva la franquicia.
  return viewerPackagePriceCents(pkg, currency, { isFranchisee: true });
}

/**
 * La columna DERECHA: el paquete solo, con su pago.
 *
 * ── Por qué el botón puede no llevar a Stripe ────────────────────────
 * Si el servidor todavía no tiene la llave de esa moneda, el endpoint
 * responde `not_configured` (200, no error) y aquí se dice con todas sus
 * letras en vez de dejar un botón que gira para siempre. El resto del
 * flujo —la franquicia, de la izquierda— sigue funcionando.
 */
export function PaqueteElegidoCard({
  pkg,
  currency,
  locale,
  onComprar,
}: {
  pkg: Package;
  currency: Currency;
  locale: ReturnType<typeof localeOf>;
  /** Lleva al checkout con SOLO este paquete. */
  onComprar: () => void;
}) {
  const { t } = useTranslation();
  const content = contentFor(pkg.translations, locale);
  // "Comprar directo" sin franquicia: quien YA la tiene paga igual su 10 % menos.
  const viewer = usePriceViewer();
  const precio = viewerPackagePriceCents(pkg, currency, viewer);
  const lista = pkg.listPrices[currency];

  return (
    /*
      ── Misma altura que la columna de al lado (Anahí, 29-ago) ─────────
      `h-full` sobre la celda y columna flex por dentro: la tarjeta se
      estira hasta donde llegue la franquicia. Se quitó el `sticky` —dos
      tarjetas de la misma altura no tienen nada que seguir al hacer
      scroll, y una tarjeta pegada que además se estira pelea consigo
      misma—.
    */
    <aside className="flex h-full flex-col">
      <section className="flex flex-1 flex-col overflow-hidden rounded-3xl border border-ds-border bg-white shadow-ds-float">
        {/* Solo la foto, el nombre y el precio: lo que pidió Anahí. */}
        <PackageArtwork imageUrl={pkg.imageUrl} name={content.name} items={[]} />

        <div className="flex flex-1 flex-col p-6">
          <p className="text-[11px] font-semibold uppercase tracking-ds-label-wide text-ds-ink-3">
            {t("Tu paquete")}
          </p>
          <h2 className="mt-1 text-[20px] font-semibold leading-tight text-ds-ink">
            {content.name}
          </h2>

          <p className="mt-3 flex flex-wrap items-baseline gap-2">
            <span className="text-[28px] font-semibold text-ds-ink">
              {typeof precio === "number" ? formatPrice(precio, currency) : "—"}
            </span>
            {typeof lista === "number" && typeof precio === "number" && lista > precio && (
              <span className="text-[14px] text-ds-ink-3 line-through">
                {formatPrice(lista, currency)}
              </span>
            )}
            <span className="text-[13px] text-ds-ink-3">{currency}</span>
          </p>

          {/* `mt-auto` empuja el cierre al fondo: con la tarjeta
              estirada, el botón queda abajo en vez de flotando a media
              caja, y a la altura del de la columna de al lado. */}
          <div className="mt-auto pt-5">
            <Button
              size="lg"
              className="w-full"
              leftIcon={<CreditCard />}
              onClick={onComprar}
            >
              {t("Comprar directo")}
            </Button>
            {/*
              El pago ya no se abre aquí: se elige en /checkout, con su
              dirección y su método. Por eso la nota dice QUÉ se compra
              —solo el paquete— y no cómo se paga, que a estas alturas
              todavía no se ha decidido.
            */}
            <p className="mt-2 flex items-start gap-1.5 text-[12px] leading-relaxed text-ds-ink-3">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t("Solo este paquete, sin franquicia. Eliges cómo pagar en el siguiente paso.")}
            </p>
          </div>
        </div>
      </section>
    </aside>
  );
}

/**
 * El bloque de la columna IZQUIERDA: qué pasa con ESTE paquete si además
 * se toma la franquicia. Es la comparación, y por eso enseña las dos
 * cifras juntas — el precio de hoy y el de franquiciatario.
 */
export function ConFranquiciaResumen({
  pkg,
  currency,
  locale,
  franchiseCents,
}: {
  pkg: Package;
  currency: Currency;
  locale: ReturnType<typeof localeOf>;
  franchiseCents: number;
}) {
  const { t } = useTranslation();
  const content = contentFor(pkg.translations, locale);
  // El "antes" es el precio de LISTA, no el de la tienda: ver el
  // comentario de `precioConFranquicia`.
  const lista = pkg.listPrices[currency];
  const conFranquicia = precioConFranquicia(pkg, currency);
  const ahorro =
    typeof lista === "number" && typeof conFranquicia === "number"
      ? Math.max(0, lista - conFranquicia)
      : 0;
  const total =
    typeof conFranquicia === "number" ? franchiseCents + conFranquicia : undefined;

  return (
    <div className="mt-5 rounded-2xl border border-ds-violet-pop/40 bg-ds-lavender-bg/60 p-5">
      <p className="flex items-center gap-2 text-[13px] font-semibold text-ds-violet-pop">
        <Sparkles className="h-4 w-4" />
        {t("Con la Franquicia Digital, {{paquete}} te sale en:", { paquete: content.name })}
      </p>

      <p className="mt-2 flex flex-wrap items-baseline gap-2">
        <span className="text-[26px] font-semibold text-ds-ink">
          {typeof conFranquicia === "number" ? formatPrice(conFranquicia, currency) : "—"}
        </span>
        {ahorro > 0 && typeof lista === "number" && (
          <>
            <span className="text-[14px] text-ds-ink-3 line-through">
              {formatPrice(lista, currency)}
            </span>
            <span className="rounded-ds-pill bg-ds-success-soft px-2 py-0.5 text-[12px] font-semibold text-ds-ink">
              {t("ahorras {{monto}}", { monto: formatPrice(ahorro, currency) })}
            </span>
          </>
        )}
      </p>

      {typeof total === "number" && (
        <dl className="mt-4 grid gap-1 border-t border-ds-violet-pop/20 pt-3 text-[13px]">
          <div className="flex justify-between">
            <dt className="text-ds-ink-3">{t("Franquicia Digital (1 año)")}</dt>
            <dd className="font-medium text-ds-ink">{formatPrice(franchiseCents, currency)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ds-ink-3">{content.name}</dt>
            <dd className="font-medium text-ds-ink">
              {formatPrice(conFranquicia as number, currency)}
            </dd>
          </div>
          <div className="mt-1 flex justify-between border-t border-ds-violet-pop/20 pt-2">
            <dt className="font-semibold text-ds-ink">{t("Total con franquicia")}</dt>
            <dd className="text-[17px] font-semibold text-ds-ink">
              {formatPrice(total, currency)}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}
